import React, { useContext, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { LocalizeContext, LocalizeContextProps, Translate } from 'react-localize-redux';
import MultiChart from './MultiChart';

interface InpatientPeriodActivitySectionProps {
  data: any;
}

const MetricCard = ({ label, value }: { label: React.ReactNode; value: string | number }) => (
  <Paper sx={{ p: 2, height: '100%' }}>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    <Typography variant="h5">{value}</Typography>
  </Paper>
);

const SERIES_KEYS = ['admissions', 'discharges', 'deaths'] as const;

const InpatientPeriodActivitySection = ({ data }: InpatientPeriodActivitySectionProps) => {
  const stats = data?.stats;
  const totals = stats?.totals;
  const rows = stats?.byDepartment || [];
  // SHER-828: getTranslate is a selector, not a hook; LocalizeContext is missing from
  // the package's typings. See analyticsTranslate.sher828.test.tsx.
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);

  const chartSeries = useMemo(() => {
    if (!stats?.chart) {
      return [];
    }
    const colors: Record<string, string> = {
      admissions: '#1E88E5',
      discharges: '#43A047',
      deaths: '#E53935',
    };
    return SERIES_KEYS.map((key) => ({
      name: translate(`hospital.analytics.inpatient_period.${key}`) as string,
      data: stats.chart.series.find((series: any) => series.name === key)?.data || [],
      color: colors[key],
    }));
  }, [stats, translate]);

  if (!totals) {
    return null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">
          <Translate id="hospital.analytics.inpatient_period.section_title" />
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <Translate id="hospital.analytics.inpatient_period.section_subtitle" />
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          <Translate id="hospital.analytics.inpatient_period.section_note" />
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.inpatient_period.admissions" />}
            value={totals.admissions}
          />
        </Grid>
        {/*
          SHER-897. Distinct patients, not admissions: a patient admitted twice in the period —
          to two departments, or readmitted — is one patient and two admissions, and the two
          cards sitting side by side is what makes that legible. It comes from the same response
          as the demographics section below, so the two cannot describe different people.
        */}
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.inpatient_period.unique_patients" />}
            value={totals.uniquePatients ?? 0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.inpatient_period.discharges" />}
            value={totals.discharges}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.inpatient_period.deaths" />}
            value={totals.deaths}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.inpatient_period.median_los" />}
            value={totals.medianLosDays}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.inpatient_period.mortality_rate" />}
            value={`${totals.mortalityRate}%`}
          />
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <MultiChart
          series={chartSeries}
          categories={stats?.chart?.categories || []}
          title={<Translate id="hospital.analytics.inpatient_period.chart_title" />}
          horizontal={false}
          type="line"
        />
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><Translate id="hospital.analytics.inpatient_period.service" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_period.admissions" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_period.discharges" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_period.deaths" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_period.median_los" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_period.mortality_rate" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.inpatient_period.average_occupancy" /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: any) => (
              <TableRow key={row.departmentUuid}>
                <TableCell>{row.departmentName}</TableCell>
                <TableCell align="right">{row.admissions}</TableCell>
                <TableCell align="right">{row.discharges}</TableCell>
                <TableCell align="right">{row.deaths}</TableCell>
                <TableCell align="right">{row.medianLosDays}</TableCell>
                <TableCell align="right">{row.mortalityRate}%</TableCell>
                <TableCell align="right">{row.averageOccupancyRate}%</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell><strong><Translate id="hospital.analytics.inpatient_period.total_row" /></strong></TableCell>
              <TableCell align="right"><strong>{totals.admissions}</strong></TableCell>
              <TableCell align="right"><strong>{totals.discharges}</strong></TableCell>
              <TableCell align="right"><strong>{totals.deaths}</strong></TableCell>
              <TableCell align="right"><strong>{totals.medianLosDays}</strong></TableCell>
              <TableCell align="right"><strong>{totals.mortalityRate}%</strong></TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default InpatientPeriodActivitySection;
